import { AsyncPipe, CommonModule, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogClose,
  MatDialogContent,
  MatDialogModule
} from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';

import { CustomHtmlComponent } from '@igo2/common/custom-html';
import { IgoInteractiveTourModule, InteractiveTourLoader, InteractiveTourService } from '@igo2/common/interactive-tour';
import { ConfigService } from '@igo2/core/config';
import { LanguageService } from '@igo2/core/language';

import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { getAppVersion } from 'src/app/app.utils';

import { WelcomeWindowService } from './welcome-window.service';
import { InteractiveTourExtensionService, InteractiveTourLoaderExtensionService } from 'src/app/shared/services/interactive-tour-extension.service';

@Component({
    selector: 'app-welcome-window',
    templateUrl: './welcome-window.component.html',
    styleUrls: ['./welcome-window.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        TranslateModule,
        IgoInteractiveTourModule,
        MatToolbarModule,
        MatDialogModule,
        MatButtonModule,
        CustomHtmlComponent
    ],
    providers: [
        { provide: InteractiveTourService, useClass: InteractiveTourExtensionService },
        { provide: InteractiveTourLoader, useClass: InteractiveTourLoaderExtensionService }
    ]
})
export class WelcomeWindowComponent implements OnInit, OnDestroy {
  // isVisible = true;
  showAgain = false;
  public discoverTitleInLocale$: Observable<string>;
  private title$$: Subscription;
  public html$ = new BehaviorSubject<string>(undefined);

  constructor(
    public dialog: MatDialog,
    private welcomeWindowService: WelcomeWindowService,
    private configService: ConfigService,
    protected languageService: LanguageService
  ) {
    this.discoverTitleInLocale$ = of(
      this.configService.getConfig(
        'welcomeWindow.discoverTitleInLocale',
        this.configService.getConfig('title')
      )
    );
  }

  ngOnInit(): void {
    this.computeHtml();
  }

  closeWelcomeWindow() {
    this.dialog.closeAll();
  }

  private computeHtml() {
    const releaseDateString = this.computeReleaseDate(
      this.configService.getConfig('version.releaseDateApp'),
      this.configService.getConfig('version.releaseDate')
    );

    this.title$$ = this.languageService.translate
      .get(this.configService.getConfig('title', ''))
      .pipe(
        map((title) => {
          return this.languageService.translate.instant('welcomeWindow.html', {
            title,
            description: this.configService.getConfig('description', ''),
            version: getAppVersion(this.configService),
            releaseDate: releaseDateString || ''
          });
        })
      )
      .subscribe((r) => this.html$.next(r));

    return this.html$;
  }

  /**
   * Date de version affichée, au format AAAA-MM-JJ.
   *
   * releaseDateApp (config SIGT) est déjà une date du calendrier : on l'affiche
   * telle quelle. La convertir en Date la place à minuit UTC, soit la veille au
   * Québec ; l'ancien « + 1 jour » compensait, mais produisait un 32 octobre à
   * chaque fin de mois.
   * releaseDate (igo2) est un horodatage : on le formate en date locale.
   */
  private computeReleaseDate(
    releaseDateApp: string | undefined,
    releaseDateLib: number | string | undefined
  ): string {
    if (releaseDateApp) {
      if (/^\d{4}-\d{2}-\d{2}$/.test(releaseDateApp)) {
        return releaseDateApp;
      }
      const date = new Date(releaseDateApp);
      if (isNaN(date.getTime())) {
        console.log('The releaseDateApp config is not a valid date format');
        return releaseDateApp;
      }
      return this.formatLocalDate(date);
    }

    if (releaseDateLib === undefined || releaseDateLib === null) {
      return '';
    }
    const date = new Date(releaseDateLib);
    return isNaN(date.getTime()) ? '' : this.formatLocalDate(date);
  }

  private formatLocalDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  setShowAgain() {
    this.welcomeWindowService.showAgain = this.showAgain;
  }

  ngOnDestroy(): void {
    if (this.title$$) {
      this.title$$.unsubscribe();
    }
  }
}
